import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  X,
  Camera,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Sparkles,
  ShieldCheck,
  Check,
  Glasses,
  Smile,
  Sun,
  Lock,
} from 'lucide-react';

interface FaceVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelfieVerified: (selfieUrl: string) => void;
  currentSelfieUrl?: string | null;
  userName?: string;
}

export const FaceVerificationModal: React.FC<FaceVerificationModalProps> = ({
  isOpen,
  onClose,
  onSelfieVerified,
  currentSelfieUrl,
  userName = 'User',
}) => {
  const [streamActive, setStreamActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  // Analysis / Verification States
  const [hasFace, setHasFace] = useState(false);
  const [noSunglasses, setNoSunglasses] = useState(true);
  const [noHat, setNoHat] = useState(true);
  const [isLightingGood, setIsLightingGood] = useState(true);
  const [statusMessage, setStatusMessage] = useState('Position your face inside the oval');

  // Automatic Verification Timer
  const [autoScanProgress, setAutoScanProgress] = useState(0); // 0 to 100%
  const [isAutoScanning, setIsAutoScanning] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(currentSelfieUrl || null);
  const [verificationPassed, setVerificationPassed] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Stop camera helper
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setStreamActive(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  // Start live front camera stream
  const startCamera = useCallback(async () => {
    setCameraError(null);
    setCapturedPhoto(null);
    setVerificationPassed(false);
    setAutoScanProgress(0);
    setIsAutoScanning(false);

    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: 'user', // Front facing camera for selfie
          width: { ideal: 720 },
          height: { ideal: 720 },
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStreamActive(true);
    } catch (err: any) {
      console.warn('Front camera request failed, trying generic video', err);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setStreamActive(true);
      } catch (fallbackErr: any) {
        const isPerm =
          fallbackErr?.message?.includes('Permission') || fallbackErr?.name === 'NotAllowedError';
        setCameraError(
          isPerm
            ? 'Camera permission denied. Please allow camera access in browser permissions or select a selfie file.'
            : 'Unable to start camera. Please verify device camera or upload a selfie file.'
        );
        setStreamActive(false);
      }
    }
  }, []);

  // Frame Capture logic
  const captureSelfieFrame = useCallback(() => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 640;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Flip horizontally for mirror reflection effect
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
    stopCamera();
    setCapturedPhoto(dataUrl);

    // Run AI Biometric Verification animation
    setIsAnalyzing(true);
    setTimeout(() => {
      setIsAnalyzing(false);
      setVerificationPassed(true);
    }, 1200);
  }, [stopCamera]);

  // Real-time Canvas Frame Analyzer for Face, Sunglasses, and Hat/Cap
  useEffect(() => {
    if (!streamActive || capturedPhoto) return;

    let isSubscribed = true;
    let scanInterval: any = null;

    const analyzeFrame = () => {
      if (!videoRef.current || !canvasRef.current || videoRef.current.readyState < 2) {
        return;
      }

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;

      const width = (canvas.width = 160);
      const height = (canvas.height = 160);

      // Draw current video frame downscaled for fast client-side pixel analysis
      ctx.drawImage(video, 0, 0, width, height);

      try {
        const imgData = ctx.getImageData(0, 0, width, height);
        const data = imgData.data;

        // Sample center region (face oval)
        let totalBrightness = 0;
        let centerPixelCount = 0;
        let skinToneCount = 0;

        // Sample eye band region (approx 35% - 48% height, 30% - 70% width)
        let eyeRegionBrightness = 0;
        let eyePixelCount = 0;

        // Sample top head/forehead band region (approx 12% - 28% height, 30% - 70% width)
        let topHeadBrightness = 0;
        let topHeadDarkPixels = 0;
        let topHeadTotal = 0;

        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const idx = (y * width + x) * 4;
            const r = data[idx];
            const g = data[idx + 1];
            const b = data[idx + 2];
            const brightness = (r * 299 + g * 587 + b * 114) / 1000;

            // Center area
            const dx = (x - width / 2) / (width * 0.35);
            const dy = (y - height / 2) / (height * 0.45);
            const inOval = dx * dx + dy * dy <= 1;

            if (inOval) {
              totalBrightness += brightness;
              centerPixelCount++;

              // Approximate skin tone check
              if (r > 60 && g > 40 && b > 20 && r > b && r > g * 0.8) {
                skinToneCount++;
              }

              // Check eye band (Y between 35% and 50%)
              if (y >= height * 0.35 && y <= height * 0.5 && x >= width * 0.28 && x <= width * 0.72) {
                eyeRegionBrightness += brightness;
                eyePixelCount++;
              }

              // Check top crown/headwear band (Y between 10% and 26%)
              if (y >= height * 0.1 && y <= height * 0.26 && x >= width * 0.25 && x <= width * 0.75) {
                topHeadBrightness += brightness;
                topHeadTotal++;
                if (brightness < 45) {
                  topHeadDarkPixels++;
                }
              }
            }
          }
        }

        const avgBrightness = centerPixelCount > 0 ? totalBrightness / centerPixelCount : 0;
        const avgEyeBrightness = eyePixelCount > 0 ? eyeRegionBrightness / eyePixelCount : 0;
        const topDarkRatio = topHeadTotal > 0 ? topHeadDarkPixels / topHeadTotal : 0;

        // Evaluation criteria:
        // 1. Face present if skin tones & brightness are balanced
        const faceDetected = centerPixelCount > 0 && avgBrightness > 40 && avgBrightness < 240;

        // 2. Sunglasses check: If eye region is excessively dark compared to the overall face
        // or below threshold 35 (sunglasses / dark shades cover)
        const eyeCovered = faceDetected && avgEyeBrightness < 38 && avgEyeBrightness < avgBrightness * 0.55;
        const noSunglassesPass = !eyeCovered;

        // 3. Hat/Cap check: If top crown of head is largely blocked or shadowed by dark brim/hat
        const hatCovered = faceDetected && topDarkRatio > 0.65;
        const noHatPass = !hatCovered;

        // 4. Lighting check
        const goodLight = avgBrightness >= 45 && avgBrightness <= 235;

        if (isSubscribed) {
          setHasFace(faceDetected);
          setNoSunglasses(noSunglassesPass);
          setNoHat(noHatPass);
          setIsLightingGood(goodLight);

          if (!faceDetected) {
            setStatusMessage('Position your face inside the oval frame');
            setIsAutoScanning(false);
            setAutoScanProgress(0);
          } else if (!noSunglassesPass) {
            setStatusMessage('⚠️ Sunglasses detected! Please remove sunglasses (সানগ্লাস খুলুন)');
            setIsAutoScanning(false);
            setAutoScanProgress(0);
          } else if (!noHatPass) {
            setStatusMessage('⚠️ Cap / Hat detected! Please remove your cap (টুপি খুলুন)');
            setIsAutoScanning(false);
            setAutoScanProgress(0);
          } else if (!goodLight) {
            setStatusMessage('⚠️ Lighting is too dim. Move to a well-lit area');
            setIsAutoScanning(false);
            setAutoScanProgress(0);
          } else {
            // ALL CHECKS PASSED: Auto-Verification Scan triggers!
            setStatusMessage('Hold steady! Auto-verifying face (স্বয়ংক্রিয় ভেরিফিকেশন)...');
            setIsAutoScanning(true);
            setAutoScanProgress((prev) => {
              if (prev >= 100) {
                captureSelfieFrame();
                return 100;
              }
              return prev + 12; // Complete in ~2.0 seconds
            });
          }
        }
      } catch (e) {
        console.debug('Frame analysis skipped', e);
      }
    };

    scanInterval = setInterval(analyzeFrame, 200);

    return () => {
      isSubscribed = false;
      if (scanInterval) clearInterval(scanInterval);
    };
  }, [streamActive, capturedPhoto, captureSelfieFrame]);

  // Open camera on modal mount
  useEffect(() => {
    if (isOpen && !currentSelfieUrl) {
      startCamera();
    } else if (isOpen && currentSelfieUrl) {
      setCapturedPhoto(currentSelfieUrl);
      setVerificationPassed(true);
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, currentSelfieUrl, startCamera, stopCamera]);

  // Handle manual file upload fallback
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      stopCamera();
      setCapturedPhoto(dataUrl);
      setIsAnalyzing(true);
      setTimeout(() => {
        setIsAnalyzing(false);
        setVerificationPassed(true);
      }, 1000);
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmVerification = () => {
    if (!capturedPhoto) return;
    onSelfieVerified(capturedPhoto);
    handleClose();
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      id="face-verification-modal"
      className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-3xl w-full max-w-md border border-slate-200 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hidden Canvas for Live Video Analysis */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Hidden file input fallback */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="user"
          onChange={handleFileUpload}
          className="hidden"
        />

        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center">
              <Camera className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-slate-900 leading-tight">
                Live Selfie Face Verification
              </h3>
              <p className="text-[11px] text-slate-500">
                সানগ্লাস ও টুপি ছাড়া স্বয়ংক্রিয় ফেস ভেরিফিকেশন
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-5 space-y-4">
          {/* Rules / Checklist Banner (No Sunglasses, No Cap) */}
          <div className="grid grid-cols-3 gap-2 text-[10px] sm:text-[11px] font-bold">
            <div
              className={`p-2 rounded-xl border flex flex-col items-center text-center transition-all ${
                noSunglasses
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                  : 'bg-rose-50 text-rose-800 border-rose-300 ring-1 ring-rose-400'
              }`}
            >
              <Glasses className="w-4 h-4 mb-1" />
              <span>No Sunglasses</span>
              <span className="text-[9px] font-normal opacity-80">সানগ্লাস ছাড়া</span>
            </div>

            <div
              className={`p-2 rounded-xl border flex flex-col items-center text-center transition-all ${
                noHat
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                  : 'bg-rose-50 text-rose-800 border-rose-300 ring-1 ring-rose-400'
              }`}
            >
              <Smile className="w-4 h-4 mb-1" />
              <span>No Hat / Cap</span>
              <span className="text-[9px] font-normal opacity-80">টুপি ছাড়া</span>
            </div>

            <div
              className={`p-2 rounded-xl border flex flex-col items-center text-center transition-all ${
                isLightingGood && hasFace
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                  : 'bg-amber-50 text-amber-800 border-amber-200'
              }`}
            >
              <Sun className="w-4 h-4 mb-1" />
              <span>Good Light</span>
              <span className="text-[9px] font-normal opacity-80">পর্যাপ্ত আলো</span>
            </div>
          </div>

          {/* CAMERA FEED & OVAL VIEWFINDER */}
          {!capturedPhoto ? (
            <div className="space-y-3">
              <div className="relative aspect-square max-w-[340px] mx-auto bg-slate-950 rounded-3xl overflow-hidden shadow-inner flex items-center justify-center">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover scale-x-[-1]"
                />

                {/* Face Alignment Oval Frame */}
                <div
                  className={`absolute inset-6 rounded-[50%] border-4 transition-all duration-300 pointer-events-none flex items-center justify-center ${
                    isAutoScanning
                      ? 'border-emerald-400 shadow-[0_0_25px_rgba(52,211,153,0.6)]'
                      : !noSunglasses || !noHat
                      ? 'border-rose-500 shadow-[0_0_20px_rgba(244,63,94,0.6)]'
                      : 'border-white/70'
                  }`}
                >
                  {/* Holographic Laser Scan Line */}
                  {isAutoScanning && (
                    <div
                      className="absolute w-full h-1 bg-gradient-to-r from-transparent via-emerald-400 to-transparent shadow-[0_0_12px_#34d399] transition-all duration-150 animate-bounce"
                      style={{ top: `${autoScanProgress}%` }}
                    />
                  )}
                </div>

                {/* Status Overlay at bottom of video */}
                <div className="absolute inset-x-4 bottom-4">
                  <div
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold text-center backdrop-blur-md transition-all shadow-md ${
                      isAutoScanning
                        ? 'bg-emerald-600/90 text-white'
                        : !noSunglasses || !noHat
                        ? 'bg-rose-600/90 text-white animate-pulse'
                        : 'bg-black/75 text-white'
                    }`}
                  >
                    {statusMessage}
                  </div>
                </div>

                {/* Auto-scan progress indicator */}
                {isAutoScanning && (
                  <div className="absolute top-4 inset-x-8">
                    <div className="bg-black/60 backdrop-blur-xs p-2 rounded-xl text-center text-white space-y-1">
                      <div className="flex items-center justify-between text-[10px] font-black">
                        <span>AUTOMATIC FACE VERIFICATION</span>
                        <span className="text-emerald-400">{autoScanProgress}%</span>
                      </div>
                      <div className="w-full bg-white/20 h-1.5 rounded-full overflow-hidden">
                        <div
                          className="bg-emerald-400 h-full rounded-full transition-all duration-200"
                          style={{ width: `${autoScanProgress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Banner when camera is unavailable */}
                {cameraError && (
                  <div className="absolute inset-0 bg-slate-950/95 flex items-center justify-center p-6 text-center">
                    <div className="space-y-3 text-white">
                      <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto" />
                      <p className="text-xs text-slate-300 leading-relaxed">{cameraError}</p>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-xl text-xs font-bold text-white shadow-md cursor-pointer"
                      >
                        Upload Selfie Photo Instead
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Control Buttons below Camera */}
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="py-2.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-colors cursor-pointer"
                  title="Upload from device"
                >
                  Upload File
                </button>

                <button
                  type="button"
                  id="btn-selfie-snap-now"
                  disabled={!streamActive}
                  onClick={captureSelfieFrame}
                  className="flex-1 py-2.5 px-4 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  <Camera className="w-4 h-4" />
                  <span>Snap Now (Manual)</span>
                </button>
              </div>
            </div>
          ) : (
            /* CAPTURED SELFIE REVIEW & BIOMETRIC CONFIRMATION */
            <div className="space-y-4 animate-in fade-in duration-200">
              <div className="relative aspect-square max-w-[340px] mx-auto bg-slate-100 rounded-3xl overflow-hidden border-2 border-emerald-500 shadow-lg">
                <img
                  src={capturedPhoto}
                  alt="Verified Selfie"
                  className="w-full h-full object-cover"
                />

                {/* Biometric Scanning Animation in Progress */}
                {isAnalyzing && (
                  <div className="absolute inset-0 bg-purple-900/60 backdrop-blur-xs flex flex-col items-center justify-center text-white space-y-3 p-4">
                    <div className="w-12 h-12 rounded-full border-4 border-white border-t-transparent animate-spin" />
                    <span className="text-xs font-black tracking-wider">
                      PREPARING SELFIE FOR KYC REVIEW...
                    </span>
                    <span className="text-[10px] text-purple-200">
                      Checking image framing & clarity before submission
                    </span>
                  </div>
                )}

                {/* Captured Stamp Badge */}
                {verificationPassed && (
                  <div className="absolute top-3 right-3 bg-amber-600 text-white text-[11px] font-black px-3 py-1 rounded-full shadow-lg flex items-center gap-1.5 animate-in zoom-in duration-300">
                    <ShieldCheck className="w-4 h-4" />
                    <span>CAPTURED • PENDING KYC</span>
                  </div>
                )}
              </div>

              {verificationPassed && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-2xl text-amber-900 text-xs space-y-1">
                  <div className="flex items-center gap-2 font-black">
                    <CheckCircle2 className="w-4 h-4 text-amber-600" />
                    <span>Selfie Captured — Pending Admin KYC Review</span>
                  </div>
                  <p className="text-[11px] text-amber-800">
                    Your live selfie snapshot has been captured and will be reviewed alongside your Government ID by the Admin KYC team.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={startCamera}
                  className="flex-1 py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Retake Selfie</span>
                </button>

                <button
                  type="button"
                  id="btn-confirm-selfie"
                  onClick={handleConfirmVerification}
                  className="flex-2 py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Check className="w-4 h-4" />
                  <span>Confirm & Save Selfie</span>
                </button>
              </div>
            </div>
          )}

          <div className="text-[11px] text-slate-500 text-center flex items-center justify-center gap-1">
            <Lock className="w-3 h-3 text-slate-400" />
            <span>Biometric selfie is encrypted and used only for KYC compliance</span>
          </div>
        </div>
      </div>
    </div>
  );
};
